import React, { useEffect, useState, useMemo } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Check,
  AlertCircle,
  AlertTriangle,
  Quote,
  TrendingUp,
  Calendar,
  BookOpen,
  FileText,
  Loader2,
} from 'lucide-react';
import { getFactsForGroup } from '../services/simpleStore';
import { LanguageCode, StoryFact, FactType, FactConfidence } from '../types';

// =============================================================================
// TYPES
// =============================================================================

interface FactsPanelProps {
  groupId: string;
  language: LanguageCode;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const RTL_LANGUAGES: LanguageCode[] = ['fa-AF', 'ps-AF'];

const FACT_TYPE_ICONS: Record<FactType, typeof FileText> = {
  claim: FileText,
  quote: Quote,
  statistic: TrendingUp,
  event: Calendar,
  background: BookOpen,
};

const FACT_TYPE_LABELS: Record<FactType, string> = {
  claim: 'Claim',
  quote: 'Quote',
  statistic: 'Statistic',
  event: 'Event',
  background: 'Background',
};

const CONFIDENCE_COLORS: Record<FactConfidence, { text: string; bg: string; icon: typeof Check }> = {
  verified: {
    text: 'text-green-600 dark:text-green-400',
    bg: 'bg-green-50 dark:bg-green-900/20',
    icon: Check,
  },
  attributed: {
    text: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    icon: FileText,
  },
  unverified: {
    text: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    icon: AlertTriangle,
  },
  disputed: {
    text: 'text-red-600 dark:text-red-400',
    bg: 'bg-red-50 dark:bg-red-900/20',
    icon: AlertCircle,
  },
};

const CONFIDENCE_LABELS: Record<FactConfidence, string> = {
  verified: 'Verified',
  attributed: 'Attributed',
  unverified: 'Unverified',
  disputed: 'Disputed',
};

// =============================================================================
// COMPONENT
// =============================================================================

const FactsPanel: React.FC<FactsPanelProps> = ({ groupId, language }) => {
  const [facts, setFacts] = useState<StoryFact[]>([]);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  const isRTL = RTL_LANGUAGES.includes(language);

  // Fetch facts on mount or when groupId changes
  useEffect(() => {
    if (!groupId) {
      setLoading(false);
      return;
    }

    const fetchFacts = async () => {
      setLoading(true);
      const result = await getFactsForGroup(groupId);
      setFacts(result);
      setLoading(false);
    };

    fetchFacts();
  }, [groupId]);

  // Calculate attention count (unverified, disputed, or partial completeness)
  const attentionCount = useMemo(() => {
    return facts.filter(
      (fact) =>
        fact.confidence === 'unverified' ||
        fact.confidence === 'disputed' ||
        fact.completeness === 'partial'
    ).length;
  }, [facts]);

  // Don't render if no facts
  if (!loading && facts.length === 0) {
    return null;
  }

  return (
    <div
      dir={isRTL ? 'rtl' : 'ltr'}
      className="bg-zinc-50 dark:bg-zinc-800/50 rounded-lg border border-zinc-200 dark:border-zinc-800"
    >
      {/* Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full p-4 flex items-center justify-between hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors rounded-lg ${
          isRTL ? 'flex-row-reverse' : ''
        }`}
        disabled={loading}
      >
        <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="p-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/20">
            <FileText className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div className={`${isRTL ? 'text-right' : 'text-left'}`}>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-zinc-900 dark:text-white text-sm">
                FACTS
              </span>
              {loading ? (
                <Loader2 className="h-3 w-3 animate-spin text-zinc-400" />
              ) : (
                <span className="text-zinc-500 dark:text-zinc-400 text-sm">
                  ({facts.length})
                </span>
              )}
              {!loading && attentionCount > 0 && (
                <>
                  <span className="text-zinc-400 dark:text-zinc-500">•</span>
                  <span className="text-amber-600 dark:text-amber-400 text-sm">
                    {attentionCount} {attentionCount === 1 ? 'needs' : 'need'} attention
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-zinc-500" />
        ) : (
          <ChevronDown className="h-4 w-4 text-zinc-500" />
        )}
      </button>

      {/* Expanded content */}
      {isExpanded && !loading && (
        <div className="border-t border-zinc-200 dark:border-zinc-700 divide-y divide-zinc-200 dark:divide-zinc-700">
          {facts.map((fact) => (
            <FactItem key={fact.id} fact={fact} isRTL={isRTL} />
          ))}
        </div>
      )}

      {/* Loading state for expanded content */}
      {isExpanded && loading && (
        <div className="border-t border-zinc-200 dark:border-zinc-700 p-4 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
          <span className="ml-2 text-sm text-zinc-500">Loading facts...</span>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// FACT ITEM COMPONENT
// =============================================================================

interface FactItemProps {
  fact: StoryFact;
  isRTL: boolean;
}

const FactItem: React.FC<FactItemProps> = ({ fact, isRTL }) => {
  const TypeIcon = FACT_TYPE_ICONS[fact.fact_type] || FileText;
  const confidenceConfig = CONFIDENCE_COLORS[fact.confidence] || CONFIDENCE_COLORS.attributed;
  const ConfidenceIcon = confidenceConfig.icon;

  const needsAttention =
    fact.confidence === 'unverified' ||
    fact.confidence === 'disputed' ||
    fact.completeness === 'partial';

  return (
    <div className={`p-4 ${needsAttention ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''}`}>
      <div className={`flex gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
        {/* Confidence indicator */}
        <div
          className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${confidenceConfig.bg}`}
        >
          <ConfidenceIcon className={`h-3.5 w-3.5 ${confidenceConfig.text}`} />
        </div>

        {/* Content */}
        <div className={`flex-1 min-w-0 ${isRTL ? 'text-right' : 'text-left'}`}>
          {/* Fact content */}
          <p className="text-sm text-zinc-900 dark:text-white leading-relaxed">
            {fact.content}
          </p>

          {/* Metadata row */}
          <div
            className={`mt-2 flex flex-wrap items-center gap-2 text-xs ${
              isRTL ? 'flex-row-reverse' : ''
            }`}
          >
            {/* Fact type badge */}
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-100 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 ${
                isRTL ? 'flex-row-reverse' : ''
              }`}
            >
              <TypeIcon className="h-3 w-3" />
              {FACT_TYPE_LABELS[fact.fact_type]}
            </span>

            {/* Confidence badge */}
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${confidenceConfig.bg} ${confidenceConfig.text}`}
            >
              {CONFIDENCE_LABELS[fact.confidence]}
            </span>

            {/* Partial completeness indicator */}
            {fact.completeness === 'partial' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400">
                <AlertTriangle className="h-3 w-3" />
                Partial
              </span>
            )}
          </div>

          {/* Source attribution */}
          {fact.source_attribution && (
            <p className={`mt-1.5 text-xs text-zinc-500 dark:text-zinc-400 ${isRTL ? 'text-right' : ''}`}>
              Source: {fact.source_attribution}
              {fact.source_type && (
                <span className="text-zinc-400 dark:text-zinc-500">
                  {' '}({fact.source_type})
                </span>
              )}
            </p>
          )}

          {/* Completeness note */}
          {fact.completeness_note && (
            <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
              {fact.completeness_note}
            </p>
          )}

          {/* Confidence note */}
          {fact.confidence_note && (
            <p className={`mt-1 text-xs ${confidenceConfig.text}`}>
              {fact.confidence_note}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default FactsPanel;
